import { Link } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useLayouts } from '@/hooks/useLayout'
import { timeAgo } from '@/lib/format'
import { HomeWidget } from '../HomeWidget'

interface LayoutSummary {
  id: string
  name: string
  updatedAt: string
  deviceCount?: number
  connectionCount?: number
  devices?: unknown[]
  connections?: unknown[]
}

/**
 * Quick-resume widget for the most-recently-updated Bussboard
 * environment. Mirrors the shape of `LatestProject` / `MidiStatus`
 * so the three fit in the home page's quick-access row. Replaces
 * the old standalone `ContinueWorking` full-width hero tile — the
 * user asked for all three widgets to live in one grid above the
 * Apps section.
 */
export function LatestLayout() {
  const { data } = useLayouts()
  const latest = useMemo(() => {
    if (!Array.isArray(data)) return null
    const sorted = [...(data as LayoutSummary[])].sort(
      (a, b) =>
        new Date(b.updatedAt ?? 0).getTime() -
        new Date(a.updatedAt ?? 0).getTime(),
    )
    return sorted[0] ?? null
  }, [data])

  if (!latest) return null

  const deviceCount = latest.deviceCount ?? latest.devices?.length ?? 0
  const connectionCount =
    latest.connectionCount ?? latest.connections?.length ?? 0

  return (
    <HomeWidget
      title="Latest environment"
      action={
        <Link
          to="/bussboard"
          className="text-[10px] font-semibold uppercase tracking-[0.14em] text-accent hover:text-[color:var(--accent-hover)]"
        >
          See all →
        </Link>
      }
    >
      <Link
        to="/bussboard/$id"
        params={{ id: latest.id }}
        className="flex flex-col gap-1 py-1.5 rounded-md hover:bg-surface-sunken px-2 -mx-2 transition-colors"
      >
        <div className="flex items-center justify-between gap-3">
          <span className="truncate text-content-primary text-sm font-medium">
            {latest.name}
          </span>
          {latest.updatedAt && (
            <span
              className="text-[11px] text-content-tertiary flex-shrink-0"
              title={new Date(latest.updatedAt).toLocaleString()}
            >
              {timeAgo(latest.updatedAt)}
            </span>
          )}
        </div>
        <div className="text-[11px] text-content-tertiary">
          {deviceCount} device{deviceCount === 1 ? '' : 's'} · {connectionCount}{' '}
          connection{connectionCount === 1 ? '' : 's'}
        </div>
      </Link>
    </HomeWidget>
  )
}
